/* File: src\components\dashboard\MonthlyStats.js */

/* src/components/dashboard/MonthlyStats.js */
import React, { useMemo } from 'react';
import { isDateInCurrentMonth } from '../../utils/dateUtils'; // Import the specific date utility
import { calculateSessionStats, formatCalculatedStats } from './utils/statsCalculator'; // Import calculation/formatting utilities

/**
 * Component to display statistics for sessions completed this month.
 */
const MonthlyStats = ({ sessions }) => {

  // Memoize the calculation of this month's sessions and stats
  const monthlyStats = useMemo(() => {
    // Filter sessions completed this month using the specific utility function
    const monthlySessions = Array.isArray(sessions)
      ? sessions.filter(session => session && isDateInCurrentMonth(session.date))
      : [];

    // Calculate stats using the shared utility function
    return calculateSessionStats(monthlySessions);

  }, [sessions]); // Recalculate only when sessions array changes

  // Format the calculated stats for display using the shared utility function
  const formattedStats = useMemo(() => formatCalculatedStats(monthlyStats), [monthlyStats]);

  // --- Empty State: No Sessions This Month ---
  if (!formattedStats) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm">
        <div className="text-center">
          <div className="w-12 h-12 mx-auto mb-3 bg-gray-100 rounded-full flex items-center justify-center text-gray-400">
             {/* Calendar Icon */}
             <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
               <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
             </svg>
          </div>
          <h3 className="text-gray-800 font-medium mb-1">No Training This Month</h3>
          <p className="text-gray-500 text-sm mb-4">Complete a session this calendar month to see your stats here.</p>
           <div className="inline-flex items-center text-xs text-gray-600 bg-gray-100 px-2 py-1 rounded">
             <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
               <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
             </svg>
             Consistency is key!
           </div>
        </div>
      </div>
    );
  }

  // --- Render Enhanced Stats Display (Structure copied from WeeklyStats/TodaysStats, using monthly formattedStats) ---
  return (
    <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-5">
      {/* Header */}
      <div className="flex justify-between items-center mb-4 pb-3 border-b border-gray-200">
        <h3 className="text-lg font-semibold text-gray-800">This Month's Training Stats</h3>
        {/* Header session count is already correct, no change needed here */}
        <div className="bg-gray-100 text-gray-600 px-3 py-1 rounded-full text-xs font-medium">
          {formattedStats.totalSessions} {formattedStats.totalSessions === 1 ? 'Session' : 'Sessions'}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        {/* Card 1: Session Overview */}
        <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
          <h4 className="text-sm text-gray-600 font-medium mb-3 flex items-center">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1.5 text-gray-500" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
            </svg>
            Session Overview
          </h4>

          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="text-gray-600">Total Time:</span>
              <span className="font-medium text-gray-800">{formattedStats.formattedTotalTime}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Avg d′ Score:</span>
              <span className="font-medium text-gray-800">{formattedStats.formattedAvgDprime}</span>
            </div>
            {/* --- START: Added Number of Sessions --- */}
            <div className="flex justify-between">
              <span className="text-gray-600">Sessions:</span>
              <span className="font-medium text-gray-800">{formattedStats.totalSessions}</span>
            </div>
            {/* --- END: Added Number of Sessions --- */}
          </div>
        </div>

        {/* Card 2: Accuracy Stats */}
        <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
          <h4 className="text-sm text-gray-600 font-medium mb-3 flex items-center">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1.5 text-gray-500" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            Accuracy Stats
          </h4>

          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="text-gray-600">Overall Accuracy:</span>
              <span className="font-medium text-gray-800">{formattedStats.formattedAvgAccuracy}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Visual Accuracy:</span>
              <span className="font-medium text-gray-800">{formattedStats.formattedAvgVisualAccuracy}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Audio Accuracy:</span>
              <span className="font-medium text-gray-800">{formattedStats.formattedAvgAudioAccuracy}</span>
            </div>
          </div>
        </div>

        {/* Card 3: Level Progress */}
        <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
          <h4 className="text-sm text-gray-600 font-medium mb-3 flex items-center">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1.5 text-gray-500" viewBox="0 0 20 20" fill="currentColor">
              <path d="M5 4a1 1 0 00-2 0v7.268a2 2 0 000 3.464V16a1 1 0 102 0v-1.268a2 2 0 000-3.464V4zM11 4a1 1 0 10-2 0v1.268a2 2 0 000 3.464V16a1 1 0 102 0V8.732a2 2 0 000-3.464V4zM16 3a1 1 0 011 1v7.268a2 2 0 010 3.464V16a1 1 0 11-2 0v-1.268a2 2 0 010-3.464V4a1 1 0 011-1z" />
            </svg>
            Level Progress
          </h4>

          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="text-gray-600">Average Level:</span>
              {/* Levels are already formatted strings */}
              <span className="font-medium text-gray-800">{formattedStats.avgStartLevel}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Highest Level:</span>
              <span className="font-medium text-gray-800">{formattedStats.highestEndLevel}</span>
            </div>
             <div className="flex justify-between">
              <span className="text-gray-600">Lowest Level:</span>
              <span className="font-medium text-gray-800">{formattedStats.lowestStartLevel}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MonthlyStats;